import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { diasDoDiaInteiro } from '../../componentes/reuniao-livre';
import { Modal } from '../../componentes/sobreposicao';
import { get } from '../../nucleo/api';
import { data as FMT, dataHora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { FormReuniaoLivre } from './FormReuniaoLivre';
import type { EventoGoogleDados } from './tipos';

/**
 * Ficha 2026-11-03 (editar e atraso): o evento criado no Google, aberto pelo bloqueio dele. Lido na hora e nunca guardado em cache:
 * é o compromisso da própria pessoa. Pode editar vai para o formulário de reunião; senão, só leitura, dizendo por quê.
 */
export function EventoDoGoogle({ bloqueioId, aoFechar }: { bloqueioId: string; aoFechar: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const q = useQuery({
    // Fora da chave da agenda: salvar e o sinal ao vivo recarregam a agenda, e este evento já pode ter virado reunião.
    queryKey: ['evento-google', bloqueioId],
    queryFn: () => get<EventoGoogleDados>(`/agenda/bloqueios/${bloqueioId}/evento-google`),
    gcTime: 0,
    staleTime: 0,
    retry: false,
    // Com o formulário aberto, a versão lida é a dos campos: nova leitura anularia a conferência de versão.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  if (q.data && !q.data.so_leitura) return <FormReuniaoLivre doGoogle={{ bloqueioId, dados: q.data }} aoFechar={aoFechar} />;
  const d = q.data;
  // Rodada rge-2 (VIS-M1): como no formulário, o modo discreto mascara o nome de quem está na reunião dentro dos textos.
  const nomes = (d?.convidados ?? []).map((c) => c.nome);
  const diaInteiro = (e: EventoGoogleDados) => {
    const dias = diasDoDiaInteiro(e, s.workspace.fuso);
    return dias.diaFinal === dias.dia ? t('reuniaoLivre.diaInteiroEm', { data: FMT(dias.dia) }) : t('reuniaoLivre.diaInteiroDe', { de: FMT(dias.dia), ate: FMT(dias.diaFinal) });
  };
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('eventoGoogle.titulo')}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => void q.refetch()} />}
      {d && (
        <div className="flex min-w-0 flex-col gap-3 text-sm" data-evento-google-leitura="">
          <p role="status" className="rounded-lg border border-borda bg-sup-2 px-3 py-2">
            {t(`eventoGoogle.motivo.${d.so_leitura}`)}
          </p>
          <p className="font-medium [overflow-wrap:anywhere]">{d.assunto ? textoLivre(d.assunto, ...nomes) : t('crm.semAssunto')}</p>
          <p className="numero">
            {d.dia_inteiro ? diaInteiro(d) : `${dataHora(d.inicio)} ${t('eventoGoogle.ate')} ${dataHora(d.fim)}`}
          </p>
          {d.local && <p className="[overflow-wrap:anywhere]">{textoLivre(d.local, ...nomes)}</p>}
          {d.observacoes && <p className="whitespace-pre-wrap text-tinta-2 [overflow-wrap:anywhere]">{textoLivre(d.observacoes, ...nomes)}</p>}
        </div>
      )}
    </Modal>
  );
}
